import { Box, Typography, Grid, MenuItem } from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";
import { CalculatorOptionsGroup } from "../types/calculatorOptionsGroup";
import useChoiceCalculator from "../hooks/useChoiceCalculator";
import CalculatorSelectFieldItem from "../components/CalculatorSelectFieldItem";

const options: CalculatorOptionsGroup[] = [
  {
    title: "1. Incomplete emptying",
    subtitle:
      "Over the past month, how often have you had a sensation of not emptying your bladder completely after you finished urinating?",
    code: "incomplete-emptying",
    required: true,
    options: [
      { display: "Not at all", code: nanoid(), result: 0 },
      { display: "Less than 1 time in 5", code: nanoid(), result: 1 },
      { display: "Less than half the time", code: nanoid(), result: 2 },
      { display: "About half the time", code: nanoid(), result: 3 },
      { display: "More than half the time", code: nanoid(), result: 4 },
      { display: "Almost always", code: nanoid(), result: 5 },
    ],
  },
  {
    title: "2. Frequency",
    subtitle:
      "Over the past month, how often have you had to urinate again less than two hours after you finished urinating?",
    code: "frequency",
    required: true,
    options: [
      { display: "Not at all", code: nanoid(), result: 0 },
      { display: "Less than 1 time in 5", code: nanoid(), result: 1 },
      { display: "Less than half the time", code: nanoid(), result: 2 },
      { display: "About half the time", code: nanoid(), result: 3 },
      { display: "More than half the time", code: nanoid(), result: 4 },
      { display: "Almost always", code: nanoid(), result: 5 },
    ],
  },
  {
    title: "3. Intermittency",
    subtitle:
      "Over the past month, how often have you found you stopped and started again several times when you urinated?",
    code: "intermittency",
    required: true,
    options: [
      { display: "Not at all", code: nanoid(), result: 0 },
      { display: "Less than 1 time in 5", code: nanoid(), result: 1 },
      { display: "Less than half the time", code: nanoid(), result: 2 },
      { display: "About half the time", code: nanoid(), result: 3 },
      { display: "More than half the time", code: nanoid(), result: 4 },
      { display: "Almost always", code: nanoid(), result: 5 },
    ],
  },
  {
    title: "4. Urgency",
    subtitle:
      "Over the past month, how often have you found it difficult to postpone urination?",
    code: "urgency",
    required: true,
    options: [
      { display: "Not at all", code: nanoid(), result: 0 },
      { display: "Less than 1 time in 5", code: nanoid(), result: 1 },
      { display: "Less than half the time", code: nanoid(), result: 2 },
      { display: "About half the time", code: nanoid(), result: 3 },
      { display: "More than half the time", code: nanoid(), result: 4 },
      { display: "Almost always", code: nanoid(), result: 5 },
    ],
  },
  {
    title: "5. Weak stream",
    subtitle:
      "Over the past month, how often have you had a weak urinary stream?",
    code: "weak-stream",
    required: true,
    options: [
      { display: "Not at all", code: nanoid(), result: 0 },
      { display: "Less than 1 time in 5", code: nanoid(), result: 1 },
      { display: "Less than half the time", code: nanoid(), result: 2 },
      { display: "About half the time", code: nanoid(), result: 3 },
      { display: "More than half the time", code: nanoid(), result: 4 },
      { display: "Almost always", code: nanoid(), result: 5 },
    ],
  },
  {
    title: "6. Straining",
    subtitle:
      "Over the past month, how often have you had to push or strain to begin urination?",
    code: "straining",
    required: true,
    options: [
      { display: "Not at all", code: nanoid(), result: 0 },
      { display: "Less than 1 time in 5", code: nanoid(), result: 1 },
      { display: "Less than half the time", code: nanoid(), result: 2 },
      { display: "About half the time", code: nanoid(), result: 3 },
      { display: "More than half the time", code: nanoid(), result: 4 },
      { display: "Almost always", code: nanoid(), result: 5 },
    ],
  },
  {
    title: "7. Nocturia",
    subtitle:
      "Over the past month, how many times did you most typically get up to urinate from the time you went to bed at night until the time you got up in the morning?",
    code: "nocturia",
    required: true,
    options: [
      { display: "None", code: nanoid(), result: 0 },
      { display: "1 time", code: nanoid(), result: 1 },
      { display: "2 times", code: nanoid(), result: 2 },
      { display: "3 times", code: nanoid(), result: 3 },
      { display: "4 times", code: nanoid(), result: 4 },
      { display: "5 or more times", code: nanoid(), result: 5 },
    ],
  },
  {
    title: "Quality of Life",
    subtitle:
      "If you were to spend the rest of your life with your urinary condition just the way it is now, how would you feel about that?",
    code: "qol",
    required: true,
    options: [
      { display: "Delighted", code: nanoid(), result: 0 },
      { display: "Pleased", code: nanoid(), result: 1 },
      { display: "Mostly satisfied", code: nanoid(), result: 2 },
      { display: "Mixed", code: nanoid(), result: 3 },
      { display: "Mostly dissatisfied", code: nanoid(), result: 4 },
      { display: "Unhappy", code: nanoid(), result: 5 },
      { display: "Terrible", code: nanoid(), result: 6 },
    ],
  },
];

export default function IPSSComponent() {
  const { selected, setSelected, resultText, incomplete } = useChoiceCalculator(
    options,
    (value, currentSelected) => {
      // The total score excludes QoL
      const qolSelection = currentSelected.qol;

      const qolScore = qolSelection ? qolSelection.result : 0;
      const symptomScore = value - qolScore;

      let severity = "";
      if (symptomScore <= 7) severity = "Mild";
      else if (symptomScore <= 19) severity = "Moderate";
      else severity = "Severe";

      return `Total Symptom Score: ${symptomScore} (${severity}). Quality of Life Score: ${qolScore}.`;
    },
  );

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex={1} overflow="auto" p={2}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          International Prostate Symptom Score (IPSS) is used to assess urinary
          symptoms and quality of life.
        </Typography>

        <Grid container spacing={2}>
          {options.map((group) => (
            <CalculatorSelectFieldItem
              key={group.code}
              text={group.title}
              value={selected[group.code]?.code || ""}
              onChange={(e) => {
                const option = group.options.find(
                  (o) => o.code === e.target.value,
                );
                setSelected((s) => ({ ...s, [group.code]: option }));
              }}
            >
              {group.options.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.display}
                </MenuItem>
              ))}
            </CalculatorSelectFieldItem>
          ))}
        </Grid>
      </Box>

      <Box
        p={2}
        sx={{
          borderTop: 1,
          borderColor: "#D3D3D3",
        }}
      >
        <Typography variant="h5">Result</Typography>
        <Typography variant="body1">
          {incomplete ? (
            <i>Please fill out all required fields</i>
          ) : (
            <>
              <b>{resultText}</b>
            </>
          )}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          *Symptom Score: 0-7: Mild; 8-19: Moderate; 20-35: Severe. QoL is
          reported separately.
        </Typography>
      </Box>
    </Box>
  );
}
